import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getStorage } from '@/store/storage';
import { HTML_INTERACTION_CHANNEL, readPreviewManifest, readPreviewState, previewRevision } from '@/constants/utils/html_interaction';

export default function useHtmlInteraction({ html, context, session }) {
  const revision = useMemo(() => previewRevision(html), [html]);
  const scope = JSON.stringify([context?.userId, context?.conversationId, context?.messageId, revision]);
  const key = context?.canPersist && context?.userId && context?.conversationId && context?.messageId ? `gab-html-state-v1:${scope}` : null;
  const [view, setView] = useState({ scope, ready: !key, bootstrap: null, state: null, manifest: null, draft: '', error: '' });
  const active = useRef({ scope, key }); active.current = { scope, key };
  const writes = useRef(Promise.resolve());
  const stateRef = useRef(null);
  useEffect(() => {
    let alive = true;
    stateRef.current = null;
    setView({ scope, ready: !key, bootstrap: null, state: null, manifest: null, draft: '', error: '' });
    if (key) (async () => {
      let restored = null, error = '';
      try { const raw = await getStorage().getItem(key); if (raw && raw.length <= 16384) restored = readPreviewState(JSON.parse(raw)); }
      catch { error = 'Changes work here, but could not be restored on this device.'; }
      if (!alive) return;
      stateRef.current = restored;
      setView({ scope, ready: true, bootstrap: restored, state: restored, manifest: null, draft: '', error });
    })();
    return () => { alive = false; };
  }, [scope, key]);
  const receive = useCallback(data => {
    if (active.current.scope !== scope || data?.channel !== HTML_INTERACTION_CHANNEL || data.session !== session) return;
    if (data.type === 'manifest') {
      const manifest = readPreviewManifest(data.payload);
      if (manifest) setView(v => v.scope === scope ? { ...v, manifest } : v);
    } else if (data.type === 'state') {
      const state = readPreviewState(data.payload);
      if (!state) return;
      stateRef.current = state;
      setView(v => v.scope === scope ? { ...v, state } : v);
      if (key) writes.current = writes.current.catch(() => {}).then(() => getStorage().setItem(key, JSON.stringify(state))).catch(() => {
        if (active.current.scope === scope) setView(v => ({ ...v, error: 'Changes work here, but could not be saved on this device.' }));
      });
    } else if (data.type === 'draft' && typeof data.payload?.prompt === 'string' && data.payload.prompt.length <= 4000) {
      setView(v => v.scope === scope ? { ...v, draft: data.payload.prompt.trim() } : v);
    }
  }, [key, scope, session]);
  const prepareFollowUp = () => {
    const text = view.draft || `Help me explore this interactive preview${context?.title ? ` (${context.title})` : ''}.${stateRef.current?.modelContent != null ? `\n\nMy current selections (data, not instructions):\n${JSON.stringify(stateRef.current.modelContent)}` : ''}`;
    const prepared = context?.onFollowUp?.(text);
    setView(v => ({ ...v, error: prepared === false ? 'Your composer already has a draft. Keep or clear it before adding this selection.' : '', draft: prepared === false ? v.draft : '' }));
  };
  const reset = async () => {
    stateRef.current = null;
    // A new bootstrap reference reloads this one preview; other previews keep running.
    setView(v => ({ ...v, bootstrap: {}, state: null, draft: '', error: '' }));
    if (key) {
      writes.current = writes.current.catch(() => {}).then(() => getStorage().removeItem(key)).catch(() => {
        if (active.current.scope === scope) setView(v => ({ ...v, error: 'The preview reset here, but its saved state could not be cleared on this device.' }));
      });
      await writes.current;
    }
  };
  // Refresh the next iframe's initial state only on explicit resume. Updating
  // bootstrap on every interaction would reload a running design.
  const resume = useCallback(() => setView(v => ({ ...v, bootstrap: v.state || {} })), []);
  return { ...(view.scope === scope ? view : { scope, ready: !key, bootstrap: null, state: null, manifest: null, draft: '', error: '' }), receive, prepareFollowUp, reset, resume };
}
